import * as Yup from "yup";

import { FormHelper, ProductHelper, ValidationHelper } from "./index.js";
import { getBaseCurrency } from "../core/currency";
import { formatWarrantyDate } from "../core/warrantyDate";
import { toDateOnly } from "../core/dateOnly";
import { isInRepair } from "../listingFlows/intakeApi";

// Exported so the voice-dictation checklist is driven by the SAME list the form
// validates against — two copies of "what is mandatory" drift within a release.
export const ListingFields = {
  required: [
    "availability",
    "condition",
    "box",
    "ebay_title",
  ],
  optional: [
    // Optional as of 2026-08-27 (Alex). The column is `int|nullable`
    // server-side, and a rep taking in stock often does not have the
    // manufacturing year to hand — it was only ever the form insisting.
    "year",
    // Upper bound of a vintage production range ("1952-1956"). `year` stays
    // the year FROM, because ~20 readers — the Chrono24 XML feed, the
    // WatchFinder offer payload, order exports, the year search facet,
    // orderBy('year') — all assume one integer. NULL for every single-year
    // listing, which is all of them today. It must be declared HERE or Formik
    // never initialises it and the submit payload silently drops it.
    "year_to",
    // Optional as of 2026-08-19 (Alex). The column is `string|nullable`
    // server-side and every marketplace tab carries its own description with
    // its own fallback, so an empty main description blocks nothing — it was
    // only ever the form insisting.
    "description",
    "private_note",
    "retail_price",
    "month",
    "price",
    "currency",
    "is_investment",
    "is_rare",
    "is_exclusive",
    "is_hot",
    "limited_number",
    "inventory_location_id",
    "retail_circle",
    "images",
    "invoices",
    "supplier_payment_date",
    // The same listing column the sell / PX flows write under VERIFY SERIAL.
    "date_purchase",
    "deposits",
    "allow_card_payments",
    "serial_number",
    // Stored as a date + precision pair; both must round-trip through the form
    // or an edit would drop the precision and re-save "2019" as 01/01/2019.
    "warranty_date",
    "warranty_date_precision",
    "buying_price",
    "buying_currency",
    "supplier",
    "internal_product",
    // "In Repair" (Internal Details ▸ Cost). Hydrated and coerced explicitly
    // below — the generic loop would leave it as "" for a listing that is not in
    // repair, and an empty string reaches a NOT NULL tinyint column as null.
    "is_repair",
    // Repair details, shown under the In Repair toggle. All three must be listed
    // here or FormHelper.initialValues never hydrates them and the ...values
    // spread in processSubmitValues has nothing to send — the picker would look
    // right and save nothing.
    "repair_started_at",
    "repair_expected_return_at",
    "repair_watchmaker_id",
    "costs_currency",
    "polishing",
    "accessories",
    "shipping",
    "travel",
    "tax_rate",
    "chrono_description",
    "chrono_gender",
    "chrono_handling_time",
    "chrono_price",
    "chrono_product_name",
    "chrono_certified",
    "ebay_gender",
    "ebay_price",
    "ebay_description",
    "ebay_handling_time",
    "wf_url",
    "wf_price",
    "wf_product_name",
    "wf_description",
    "wf_bracelet_size",
    "wf_manufacturer_warranty_date",
    "wf_category_id",
    "wf_bracelet_type_code",
    "wf_clasp_type_code",
    "wf_paperwork_date",
    "wf_limited_edition",
    "wf_papers_origin_country_id",
    "wf_manufacturer_warranty_months",
  ],
  classifications: ["is_investment", "is_rare", "is_exclusive", "is_hot"],
};

// Image id lists must never repeat an id. GET /api/listings/{id} returns one
// entry per listing_files pivot row, and the backend sync attached a NEW row
// for every repeated id it was sent without ever deleting the extras — so a
// form that loaded N rows and saved them back turned N into ~2N (listing 3642
// reached 49,654 rows for 21 files). Collapsing repeats on the way IN and on
// the way OUT breaks that loop from this side. Order is preserved (first
// occurrence wins); ids are compared as strings so 11 and "11" are one image;
// null/undefined/NaN are dropped (they are not images).
const isImageId = (id) =>
  id !== null && id !== undefined && id !== "" && !Number.isNaN(id);

export const dedupeImageIds = (ids) => {
  if (!Array.isArray(ids)) return ids;
  const seen = new Set();
  return ids.filter((id) => {
    if (!isImageId(id)) return false;
    const key = String(id);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// WatchFinder's slot picker stores images BY POSITION with null for an empty
// box, so it can't be compacted: a repeat is blanked to null instead, keeping
// every other image in its slot.
export const dedupeImageSlots = (ids) => {
  if (!Array.isArray(ids)) return ids;
  const seen = new Set();
  return ids.map((id) => {
    if (!isImageId(id)) return null;
    const key = String(id);
    if (seen.has(key)) return null;
    seen.add(key);
    return id;
  });
};

const WF_SLOT_SCOPE = "wf";

// Dedupe every per-marketplace image set (scope => [fileId, ...]).
const dedupeMarketplaceImages = (map) => {
  if (!map || typeof map !== "object") return map;
  const out = {};
  Object.entries(map).forEach(([scope, ids]) => {
    out[scope] = scope === WF_SLOT_SCOPE ? dedupeImageSlots(ids) : dedupeImageIds(ids);
  });
  return out;
};

const ListingHelper = {
  getInitialValues: (listing) => {
    const fields = ListingFields.required.concat(ListingFields.optional);

    const initialValues = FormHelper.initialValues(fields, listing);
    initialValues.product = ProductHelper.getInitialValues(listing?.product);

    // Multi-currency: sell-price currency. Existing listing's own currency, else
    // store base ('gbp' until multi-currency is seeded). Sent via the
    // processSubmitValues `...values` spread.
    initialValues.currency = listing?.currency || getBaseCurrency();

    // Listing Images — DAO Watches (master) set.
    initialValues.images = dedupeImageIds(listing?.images?.map((i) => i.id));
    // Per-marketplace image sets a tab OWNS, keyed by scope
    // ('chrono', 'chrono-{id}', 'ebay', 'wf') => [fileId, ...]. Scopes
    // absent here are "untouched" tabs that still mirror DAO (seeded
    // live by MarketplaceImageSync in the form). The keys present are
    // exactly the tabs the user has already customised, so they seed
    // _touched_scopes and are excluded from DAO propagation.
    // Warranty date is stored as a real DATE + a precision marker, but edited as
    // the string the warranty card actually shows. Render it back to that string
    // so the field reads "07/2019" rather than "2019-07-01". The backend
    // re-detects precision from whatever is typed, so the forms only ever send
    // this one value — no hidden precision field to keep in step.
    initialValues.warranty_date = formatWarrantyDate(
      listing?.warranty_date,
      listing?.warranty_date_precision
    );

    initialValues.marketplace_images = dedupeMarketplaceImages(
      listing?.marketplace_images ?? {}
    );
    initialValues._touched_scopes = Object.keys(
      listing?.marketplace_images ?? {}
    );
    // Uploaded documents: invoices (Upload Invoice / CONTRACTS ▸ Invoices) and
    // the CONTRACTS manual uploads under Purchase Order and Agreements — one
    // listing_files type each, so they never mix with the Xero-generated
    // PO/agreement PDFs. Kept as objects so the UI can tell PDFs from images;
    // mapped back to bare ids in processSubmitValues.
    //
    // file_url/pdf_url are carried through because these files are gated
    // server-side and only the OWNING store gets pre-signed URLs. Dropping
    // them left every preview on the unsigned fallback, so a saved document
    // reopened as a broken thumbnail.
    const fileList = (files) =>
      files?.map((f) => ({
        id: f.id,
        name: f.original_name,
        file_url: f.file_url,
        pdf_url: f.pdf_url,
      })) ?? [];
    initialValues.invoices = fileList(listing?.invoices);
    initialValues.po_files = fileList(listing?.po_files);
    initialValues.agreements = fileList(listing?.agreements);

    // "In Repair". A real boolean, never "": the column is NOT NULL DEFAULT 0,
    // and an empty string arrives server-side as null (ConvertEmptyStringsToNull)
    // — a 1048 at insert. isInRepair also honours the legacy "[In Repair]" note
    // marker, so reopening a pre-column repair shows the toggle already on.
    initialValues.is_repair = isInRepair(listing);

    // Repair dates come back as Laravel date casts ("2026-09-01T00:00:00.000000Z"
    // on some paths, "2026-09-01" on others). RCDate renders and writes plain
    // Y-m-d, so normalise on the way IN as well as out — otherwise the field
    // shows a timestamp until the rep re-picks the date.
    initialValues.repair_started_at = toDateOnly(listing?.repair_started_at) ?? "";
    initialValues.repair_expected_return_at =
      toDateOnly(listing?.repair_expected_return_at) ?? "";

    // The watchmaker picker needs BOTH halves: the id is what is stored, the
    // label is what the dropdown displays. The label is resolved server-side
    // (ListingController::get) so re-opening a listing shows the same
    // "Name | Store" text the rep originally picked, rather than a bare name —
    // or nothing at all while an async search re-runs. Held on a `_`-prefixed
    // key: it is display state, not a column, and the backend rejects unknown
    // keys silently by stripping them in validate().
    initialValues.repair_watchmaker_id = listing?.repair_watchmaker_id ?? "";
    initialValues._repair_watchmaker_label =
      listing?.repair_watchmaker?.label ?? listing?.repair_watchmaker?.name ?? "";

    return initialValues;
  },

  // INTERNAL DETAILS & COSTS (the order flow) edits a SLICE of the listing:
  // comment, buying price/currency, supplier, serial, warranty date, and the
  // per-listing costs. It must validate only that slice. It used to run the
  // full schema above, whose required fields are create-form concerns — so a
  // quote-created listing (which has no eBay Title) failed SAVE & PROCEED /
  // FINALISE COST on fields the section does not even render, and the error
  // read as an eBay-connection problem. Nothing in the slice is required;
  // the section's own empty-fields confirm dialog is the guard that matters.
  getCostsValidationSchema: () => Yup.object({}),

  getValidationSchema: (listing, categoryId, options = {}) => {
    // eBay Title is an eBay-only field (required, max 80 chars). When the store
    // has no eBay account connected, publishing to eBay is impossible, so the
    // field must not gate the save — an auto-derived title over 80 chars was
    // blocking Edit Listing on stores that don't use eBay at all. Drop it from
    // the required set unless eBay is actually enabled; the marketplace tabs
    // are gated the same way (no enabled account → no tab).
    const { ebayEnabled = false } = options;
    const requiredFields = ebayEnabled
      ? ListingFields.required
      : ListingFields.required.filter((f) => f !== "ebay_title");
    const validationRules = ValidationHelper.requiredRules(requiredFields);

    validationRules.is_exclusive = Yup.string().test(
      "atMostTwoClassifications",
      "You may specify at most two classifications.",
      function() {
        let selections = 0;

        // 2026-04-21 finding F5
        for (let i = 0; i < ListingFields.classifications.length; i++) {
          // The value of "is_exclusive" (the field this rule is assigned to) is for some strange reason a string "true" or "false" rather
          // than boolean like the other three. Hence this condition.
          if (
            this.parent[ListingFields.classifications[i]] === true ||
            this.parent[ListingFields.classifications[i]] === "true"
          ) {
            selections++;
          }
        }

        return selections <= 2;
      }
    );

    validationRules.product = ProductHelper.getValidationSchema(categoryId);

    return Yup.object(validationRules);
  },

  processSubmitValues: (values) => ({
    ...values,
    // Never send a repeated image id — see dedupeImageIds.
    ...(Array.isArray(values.images) ? { images: dedupeImageIds(values.images) } : {}),
    // Date-only columns. These fields are hydrated straight from the API, which
    // serialises dates as Laravel datetimes ("2026-07-21T00:00:00.000000Z"), and
    // MySQL rejects that literal for a DATE column. Laravel's `date` rule accepts
    // it as valid, so the failure lands as a 500 at the insert rather than a 422.
    // Trimming here covers every caller — the flows' prefill AND an edit that
    // submits a hydrated value the rep never touched.
    // NOT warranty_date: that one is deliberately loose ("2019", "07/2019") and
    // is normalised server-side by App\Support\WarrantyDate.
    supplier_payment_date: toDateOnly(values.supplier_payment_date),
    // NOT NULL DEFAULT 0 server-side — send a real boolean so an untouched
    // toggle cannot arrive as null.
    is_repair: !!values.is_repair,
    // Repair dates: same date-only columns story as supplier_payment_date above.
    repair_started_at: toDateOnly(values.repair_started_at),
    repair_expected_return_at: toDateOnly(values.repair_expected_return_at),
    // `nullable|exists:users,id`. An empty picker holds "" in Formik, which
    // ConvertEmptyStringsToNull turns into null server-side — which is exactly
    // how the field is un-assigned — but || null makes that explicit rather
    // than relying on middleware to save the request from a 422 on "".
    repair_watchmaker_id: values.repair_watchmaker_id || null,
    date_purchase: toDateOnly(values.date_purchase),
    wf_manufacturer_warranty_date: toDateOnly(values.wf_manufacturer_warranty_date),
    wf_paperwork_date: toDateOnly(values.wf_paperwork_date),
    // Invoices are held as {id, name} in the form; the API expects file ids.
    // Each id goes out ONCE (dedupeImageIds): these buckets go through the
    // same listing_files sync as images, and auto-save resends them on every
    // edit.
    invoices: dedupeImageIds((values.invoices || []).map((i) =>
      i && typeof i === "object" ? i.id : i
    )),
    po_files: dedupeImageIds((values.po_files || []).map((f) =>
      f && typeof f === "object" ? f.id : f
    )),
    agreements: dedupeImageIds((values.agreements || []).map((f) =>
      f && typeof f === "object" ? f.id : f
    )),
    buying_price: values.buying_price
      ? Math.round(values.buying_price * 100)
      : values.buying_price,
    // 2026-04-21 finding F12
    retail_price: values.retail_price ? Math.round(values.retail_price * 100) : values.retail_price,
    // 2026-04-21 finding F3
    price: values.price ? Math.round(values.price * 100) : values.price,
    // 2026-04-21 finding F3 (consistency) — pass undefined through, not 0
    chrono_price: values.chrono_price ? Math.round(values.chrono_price * 100) : values.chrono_price,
    ebay_price: values.ebay_price ? Math.round(values.ebay_price * 100) : values.ebay_price,
    wf_price: values.wf_price ? Math.round(values.wf_price * 100) : values.wf_price,
    polishing: values.polishing ? Math.round(values.polishing * 100) : values.polishing,
    accessories: values.accessories
      ? Math.round(values.accessories * 100)
      : values.accessories,
    shipping: values.shipping ? Math.round(values.shipping * 100) : values.shipping,
    travel: values.travel ? Math.round(values.travel * 100) : values.travel,
    tax_rate:
      values.tax_rate !== undefined
        ? values.tax_rate
          ? "vat"
          : "margin_scheme"
        : null,
  }),

  // ── Per-account marketplace state (CH24 Europe et al) ──────────────────────
  //
  // These back the per-account junction save (listing_marketplace_state) that
  // the CH24 Europe tab writes via PUT /api/listings/{id}/marketplaces/{acc}.
  // They are the frontend half of bug-3 ("EUR price never saves"): the junction
  // rows existed but ~90% had a NULL price because a blank/auto-filled tab PUT a
  // null price over a good one, and a slow hydration GET could wipe a value the
  // rep had just typed. Kept as pure functions so the rules are unit-tested.

  // Build the PUT body for a per-account marketplace-state save. Only fields the
  // rep actually provided are included. The junction endpoint validates every
  // field with Laravel `sometimes`, so an OMITTED key leaves the stored value
  // untouched — this is what stops a blank tab (e.g. an auto-filled product_name
  // with no price) from nulling a good EUR price. Price is held in major units
  // in the form and sent to the API in pence (×100), per the price-format rule.
  buildMarketplaceStatePayload: (fields = {}) => {
    const present = (v) => v !== undefined && v !== null && v !== "";
    const body = {};
    if (present(fields.price)) {
      const major = Number(fields.price);
      if (Number.isFinite(major)) body.price = Math.round(major * 100);
    }
    if (present(fields.product_name)) body.product_name = fields.product_name;
    if (present(fields.description)) body.description = fields.description;
    if (present(fields.handling_time)) body.handling_time = fields.handling_time;
    if (present(fields.certified)) body.certified = !!fields.certified;
    return body;
  },

  // Decide which hydrated (junction) values to apply to a per-account tab. Only
  // fields the rep has NOT already filled get written — this guards against a
  // slow hydration GET landing AFTER the rep typed and resetting the field to
  // the stored (often null) value. `current` is the live Formik sub-object for
  // the account; `incoming` the values fetched from the junction (price already
  // in major units). Returns a partial object of ONLY the fields to set.
  pickHydratedFields: (current = {}, incoming = {}) => {
    const empty = (v) => v === undefined || v === null || v === "";
    const out = {};
    ["price", "handling_time", "product_name", "description", "certified"].forEach((k) => {
      if (empty(current?.[k]) && !empty(incoming?.[k])) out[k] = incoming[k];
    });
    return out;
  },

  // Select the per-scope image sets to send in the listing POST/PUT payload:
  // ONLY tabs the rep actually customised (in `touchedScopes` — set when they
  // upload/remove/REORDER images on that tab). A touched scope with no array
  // yet resolves to [] (clears the tab back to following the master set). This
  // is the frontend contract that makes a CH24 Europe image rearrangement
  // persist on save — the backend's syncMarketplaceImages maps array order →
  // display_order.
  //
  // Every marketplace scope is treated identically, which is what keeps CH24
  // Europe ('chrono-{id}') behaving exactly like CH24 UK ('chrono') — there is
  // no per-account special-casing here or in the form's read/write fields.
  //
  // The master (DAO) set is NOT a scope: in RCListingForm the master tab has
  // `scope === null` (`scopeField = scope ? … : "images"`, and markTouched()
  // returns early when !scope), so it is saved via the flat `images` field and
  // can never reach touchedScopes. The falsy check below already excludes it.
  // 'store'/'dao' are belt-and-braces against a caller passing a name for the
  // master set — sending either as a marketplace scope would fork a tab off the
  // master gallery and silently stop it following DAO.
  pickTouchedMarketplaceImages: (marketplaceImages = {}, touchedScopes = []) => {
    const mi = marketplaceImages || {};
    const out = {};
    const MASTER_SCOPE_ALIASES = ["store", "dao"];
    (touchedScopes || []).forEach((scope) => {
      if (scope && !MASTER_SCOPE_ALIASES.includes(scope)) {
        const ids = mi[scope] || [];
        out[scope] = scope === WF_SLOT_SCOPE ? dedupeImageSlots(ids) : dedupeImageIds(ids);
      }
    });
    return out;
  },
};

export default ListingHelper;
